import React from 'react';
import styled from 'styled-components';

import { FractionalStar } from 'components/core/fractional-star';

type StarRatingProps = {
  /** Average rating to display, e.g. 4.5. Clamped to [0, max]. */
  value: number;
  max?: number;
  size?: number;
  /** Render the numeric value (e.g. "4.5") after the stars. */
  showValue?: boolean;
  className?: string;
};

/**
 * Read-only star rating display supporting fractional values (e.g. 4.5),
 * built from the shared `FractionalStar` primitive.
 */
export const StarRating = ({
  value,
  max = 5,
  size = 16,
  showValue = false,
  className,
}: StarRatingProps): JSX.Element => {
  const clamped = Math.min(max, Math.max(0, value));

  return (
    <Row className={className} role='img' aria-label={`${clamped} out of ${max} stars`}>
      {Array.from({ length: max }, (_, index) => (
        <FractionalStar key={index} fillPercent={(clamped - index) * 100} size={size} />
      ))}
      {showValue && <Value>{clamped.toFixed(1)}</Value>}
    </Row>
  );
};

const Row = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 2px;
`;

const Value = styled.span`
  margin-left: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.grey[30]};
`;
